// Schema Generators for GCC Tax Laws
import { getCurrentDomain } from '../utils/seoConfig'
// Schema for Articles
export const generateArticleSchema = (article) => {
  const countryName = getCountryName(article.slug)
  const lawName = getLawName(article.slug)

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Legislation',
    '@id': `https://${getCurrentDomain()}/articles/${article.slug}`,
    name: article.title,
    headline: article.title,
    description:
      article.metaDescription ||
      article.summary ||
      extractFirstSentence(article.content),
    url: `https://${getCurrentDomain()}/articles/${article.slug}`,
    datePublished: formatDate(article.updatedAt),
    dateModified: formatDate(article.updatedAt),
    author: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: `https://${getCurrentDomain()}`,
      logo: {
        '@type': 'ImageObject',
        url: `https://${getCurrentDomain()}/web-app-manifest-512x512.png`,
        width: 180,
        height: 60,
      },
      alternateName: 'GTL',
    },
    publisher: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: `https://${getCurrentDomain()}`,
      logo: {
        '@type': 'ImageObject',
        url: `https://${getCurrentDomain()}/web-app-manifest-512x512.png`,
        width: 180,
        height: 60,
      },
      alternateName: 'GTL',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://${getCurrentDomain()}/articles/${article.slug}`,
    },
    about: {
      '@type': 'Thing',
      name: 'Tax Law',
      description: `${countryName} tax legislation and regulations`,
    },
    mentions: [
      {
        '@type': 'Organization',
        name: getIssuingAuthority(article.slug),
      },
      {
        '@type': 'Country',
        name: countryName,
      },
    ],
    legislationType: 'Tax Law Article',
    legislationJurisdiction: {
      '@type': 'Country',
      name: countryName,
    },
    inLanguage: {
      '@type': 'Language',
      name: 'English',
      alternateName: 'en',
    },
    isPartOf: {
      '@type': 'Legislation',
      name: lawName,
      legislationJurisdiction: countryName,
    },
    keywords: article.metaKeywords || generateKeywords(article, 'article'),
    // Add structured data for article number if available
    ...(article.number && {
      identifier: {
        '@type': 'PropertyValue',
        name: 'Article Number',
        value: article.number,
      },
    }),
    // Add word count for better content understanding
    ...(article.content && {
      wordCount: getWordCount(article.content),
    }),
  }

  // Add breadcrumb if path exists
  if (article.path && article.path.length > 0) {
    schema.breadcrumb = {
      '@type': 'BreadcrumbList',
      itemListElement: article.path.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: `https://${getCurrentDomain()}/laws/${getCountryCode(
          article.slug,
        )}/${getLawShortName(article.slug)}/${item.fullPath}`,
      })),
    }
  }

  // Add related documents if available
  if (article.relatedDocs?.length > 0) {
    schema.hasPart = article.relatedDocs.map((related) => ({
      '@type': 'CreativeWork',
      url: `https://${getCurrentDomain()}${related.slug}`,
    }))
  }

  return schema
}

// Schema for Decisions
export const generateDecisionSchema = (decision) => {
  const countryName = getCountryName(decision.slug)
  const lawName = getLawName(decision.slug)
  return {
    '@context': 'https://schema.org',
    '@type': 'Legislation',
    '@id': `https://${getCurrentDomain()}/decisions/${decision.slug}`,
    name: decision.title,
    headline: decision.name,
    description:
      decision.metaDescription ||
      decision.summary ||
      extractFirstSentence(decision.content),
    url: `https://${getCurrentDomain()}/decisions/${decision.slug}`,
    datePublished: formatDate(decision.updatedAt),
    dateModified: formatDate(decision.updatedAt),
    dateCreated: formatDate(decision.updatedAt),
    legislationType: decision.type || 'Tax Decision',
    legislationJurisdiction: {
      '@type': 'Country',
      name: countryName,
    },
    author: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: `https://${getCurrentDomain()}`,
      logo: {
        '@type': 'ImageObject',
        url: `https://${getCurrentDomain()}/web-app-manifest-512x512.png`,
        width: 180,
        height: 60,
      },
      alternateName: 'GTL',
    },
    publisher: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: `https://${getCurrentDomain()}`,
      logo: {
        '@type': 'ImageObject',
        url: `https://${getCurrentDomain()}/web-app-manifest-512x512.png`,
        width: 180,
        height: 60,
      },
      alternateName: 'GTL',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://${getCurrentDomain()}/decisions/${decision.slug}`,
    },
    about: {
      '@type': 'Thing',
      name: 'Tax Decision',
      description: `${countryName} official tax authority ruling and decision`,
    },
    mentions: [
      {
        '@type': 'Organization',
        name: getIssuingAuthority(decision.slug),
      },
      {
        '@type': 'Country',
        name: countryName,
      },
    ],
    inLanguage: {
      '@type': 'Language',
      name: 'English',
      alternateName: 'en',
    },
    isBasedOn: {
      '@type': 'Legislation',
      name: lawName,
    },
    keywords: decision.metaKeywords || generateKeywords(decision, 'decision'),
    // Add decision-specific identifiers
    ...(decision.number && {
      identifier: {
        '@type': 'PropertyValue',
        name: 'Decision Number',
        value: `${decision.type.split('-')[1]} ${decision.number} of ${
          decision.year
        }`.trim(),
      },
    }),
    ...(decision.year && {
      temporalCoverage: decision.year.toString(),
    }),
    // Add decision category
    ...(decision.category && {
      category: decision.category,
    }),
    // Add word count
    ...(decision.content && {
      wordCount: getWordCount(decision.content),
    }),
    ...(decision.relatedDocs?.length > 0 && {
      hasPart: decision.relatedDocs.map((related) => ({
        '@type': 'CreativeWork',
        url: `https://${getCurrentDomain()}${related.slug}`,
      })),
    }),
  }
}

// Schema for Guidelines
export const generateGuidelineSchema = (guideline) => {
  const countryName = getCountryName(guideline.slug)
  const lawName = getLawName(guideline.slug)
  return {
    '@context': 'https://schema.org',
    '@type': 'Legislation',
    '@id': `https://${getCurrentDomain()}/guidances/${guideline.slug}`,
    name: guideline.title,
    headline: guideline.metaTitle || guideline.title,
    description:
      guideline.metaDescription ||
      guideline.summary ||
      extractFirstSentence(guideline.content),
    url: `https://${getCurrentDomain()}/guidances/${guideline.slug}`,
    datePublished: formatDate(guideline.updatedAt),
    dateModified: formatDate(guideline.updatedAt),
    provider: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: `https://${getCurrentDomain()}`,
      logo: {
        '@type': 'ImageObject',
        url: `https://${getCurrentDomain()}/web-app-manifest-512x512.png`,
        width: 180,
        height: 60,
      },
      alternateName: 'GTL',
    },
    publisher: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: `https://${getCurrentDomain()}`,
      logo: {
        '@type': 'ImageObject',
        url: `https://${getCurrentDomain()}/web-app-manifest-512x512.png`,
        width: 180,
        height: 60,
      },
      alternateName: 'GTL',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://${getCurrentDomain()}/guidances/${guideline.slug}`,
    },
    about: {
      '@type': 'Thing',
      name: 'Tax Guidance',
      description: `${countryName} tax guidance and procedures`,
    },
    mentions: [
      {
        '@type': 'Organization',
        name: getIssuingAuthority(guideline.slug),
      },
      {
        '@type': 'Country',
        name: countryName,
      },
    ],
    legislationType: guideline.type || 'Tax Guidance',
    // category: 'Tax Compliance',
    legislationJurisdiction: {
      '@type': 'Country',
      name: countryName,
    },
    inLanguage: {
      '@type': 'Language',
      name: 'English',
      alternateName: 'en',
    },
    keywords: guideline.metaKeywords || generateKeywords(guideline, 'guidance'),
    // Add guidance-specific identifiers
    ...(guideline.uniqueCode && {
      identifier: {
        '@type': 'PropertyValue',
        name: 'Guidance Code',
        value: guideline.uniqueCode,
      },
    }),
    // Add related law information
    ...(guideline.lawSlug && {
      hasPart: {
        '@type': 'Legislation',
        name: getLawName(guideline.lawSlug),
      },
    }),
    // Add audience information
    audience: [
      { '@type': 'Audience', audienceType: 'Taxpayers' },
      { '@type': 'Audience', audienceType: 'Tax Advisors' },
      { '@type': 'Audience', audienceType: 'Businesses' },
    ],
    // Add word count
    ...(guideline.content && {
      wordCount: getWordCount(guideline.content),
    }),
    ...(guideline.relatedDocs?.length > 0 && {
      hasPart: guideline.relatedDocs.map((related) => ({
        '@type': 'CreativeWork',
        url: `https://${getCurrentDomain()}${related.slug}`,
      })),
    }),
  }
}

// Schema for Tax Treaties
export const generateTreatySchema = (treaty) => {
  const isOfficial = treaty.officialTranslation !== false

  return {
    '@context': 'https://schema.org',
    '@type': 'Legislation',
    '@id': `https://${getCurrentDomain()}/tax-treaties/${treaty.slug}`,
    name: treaty.metaTitle,
    headline: treaty.metaTitle || treaty.title,
    description:
      treaty.metaDescription ||
      treaty.summary ||
      `Tax Treaty or Double Taxation Avoidance Agreement between ${treaty.country1Name} and ${treaty.country2Name}`,
    url: `https://${getCurrentDomain()}/tax-treaties/${treaty.slug}`,
    datePublished: formatDate(treaty.updatedAt),
    dateModified: formatDate(treaty.updatedAt),
    author: [
      {
        '@type': 'Organization',
        name: `Government of ${treaty.country1Name}`,
      },
      {
        '@type': 'Organization',
        name: `Government of ${treaty.country2Name}`,
      },
    ],
    publisher: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: `https://${getCurrentDomain()}`,
      logo: {
        '@type': 'ImageObject',
        url: `https://${getCurrentDomain()}/web-app-manifest-512x512.png`,
        width: 180,
        height: 60,
      },
      alternateName: 'GTL',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://${getCurrentDomain()}/tax-treaties/${treaty.slug}`,
    },
    legislationType: 'International Tax Treaty',
    legislationJurisdiction: [
      {
        '@type': 'Country',
        name: treaty.country1Name,
      },
      {
        '@type': 'Country',
        name: treaty.country2Name,
      },
    ],
    inLanguage: {
      '@type': 'Language',
      name: 'English',
      alternateName: 'en',
    },
    about: {
      '@type': 'Thing',
      name: 'Double Taxation Avoidance',
      description: 'International tax treaty to prevent double taxation',
    },
    mentions: [
      {
        '@type': 'Country',
        name: treaty.country1Name,
      },
      {
        '@type': 'Country',
        name: treaty.country2Name,
      },
    ],
    keywords: treaty.metaKeywords || generateKeywords(treaty, 'treaty'),
    identifier: {
      '@type': 'PropertyValue',
      name: 'Tax Treaty Code',
      value: `${treaty.slug}`,
    },
    // Add treaty-specific information
    ...(treaty.updatedAt && {
      dateCreated: formatDate(treaty.updatedAt),
    }),
    // Add translation status
    translationOfWork: isOfficial
      ? undefined
      : {
          '@type': 'CreativeWork',
          name: `Original ${treaty.country1Name} - ${treaty.country2Name} Tax Treaty`,
          inLanguage: 'Original Language',
        },
    // Add MLI information if available
    ...(treaty.mliContent && {
      hasPart: {
        '@type': 'LegalDocument',
        name: 'Multilateral Instrument (MLI) Provisions',
        description:
          'OECD Multilateral Convention provisions applicable to this treaty',
      },
    }),
    // Add bilateral relationship
    isPartOf: {
      '@type': 'Collection',
      name: `${treaty.country1Name} - ${treaty.country2Name} Double Taxation Agreements`,
      description: `Collection of bilateral tax treaties signed by ${treaty.country1Name} and ${treaty.country2Name}`,
    },
    // Add word count
    ...(treaty.content && {
      wordCount: getWordCount(treaty.content),
    }),
    ...(treaty.relatedDocs?.length > 0 && {
      hasPart: treaty.relatedDocs.map((related) => ({
        '@type': 'CreativeWork',
        url: `https://${getCurrentDomain()}${related.slug}`,
      })),
    }),
  }
}

// Schema for individual blog posts
export const generateBlogPostSchema = (blog) => {
  const baseUrl = `https://${getCurrentDomain()}`

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${baseUrl}/blogs/${blog.slug}`,
    headline: blog.title,
    name: blog.subtitle || blog.title,
    description: blog.description,
    articleBody: blog.content,
    url: `${baseUrl}/blogs/${blog.slug}`,
    image: {
      '@type': 'ImageObject',
      url: blog.imageUrl || `${baseUrl}/web-app-manifest-512x512.png`,
      width: 1200,
      height: 630,
    },
    datePublished: formatDate(blog.publishedDate),
    dateModified: formatDate(blog.updatedAt || blog.publishedDate),
    author: {
      '@type': 'Person',
      name: blog.author,
      // url: `${baseUrl}/authors/${blog.author
      //   .toLowerCase()
      //   .replace(/\s+/g, '-')}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/web-app-manifest-512x512.png`,
        width: 180,
        height: 60,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${baseUrl}/blogs/${blog.slug}`,
    },
    about: {
      '@type': 'Thing',
      name: blog.category,
      description: blog.description,
    },
    articleSection: blog.category,
    keywords:
      blog.metaKeywords || blog.tags?.join(', ') || generateBlogKeywords(blog),
    inLanguage: 'en',
    ...(blog.content && {
      timeRequired: `PT${Math.ceil(getWordCount(blog.content) / 200)}M`, // ISO 8601 duration format
    }),
    ...(blog.views && {
      interactionStatistic: {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/ReadAction',
        userInteractionCount: blog.views,
      },
    }),
  }
}

// Schema for blog listing page
export const generateBlogListingSchema = (blogs, searchQuery, category) => {
  const baseUrl = `https://${getCurrentDomain()}`

  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${baseUrl}/blogs?section=team`,
    name: 'GCC Tax Laws Blog',
    alternateName: 'ClariTax',
    description:
      'From treaty interpretation to compliance checklists, we break down complex tax matters with clarity and context. Our content is curated for professionals, entrepreneurs, students, and policymakers alike. Every article is backed by trusted sources, expert research, and regional relevance.',
    url: `${baseUrl}/blogs?section=team`,
    publisher: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/web-app-manifest-512x512.png`,
        width: 180,
        height: 60,
      },
    },
    inLanguage: 'en',
    about: [
      {
        '@type': 'Thing',
        name: 'Corporate Tax',
        description: 'Corporate income tax laws and regulations in the GCC',
      },
      {
        '@type': 'Thing',
        name: 'VAT',
        description:
          'Value Added Tax implementation and compliance in GCC countries',
      },
      {
        '@type': 'Thing',
        name: 'International Tax',
        description: 'Cross-border tax issues and international tax treaties',
      },
      {
        '@type': 'Thing',
        name: 'Tax Compliance',
        description: 'Tax compliance requirements and best practices',
      },
    ],
    keywords:
      'GCC tax blog, UAE tax insights, Saudi Arabia tax updates, Qatar tax news, Kuwait tax regulations, Bahrain tax compliance, Oman tax law, Middle East tax analysis, corporate tax updates, VAT insights, excise tax news, transfer pricing, tax treaties, FTA decisions, tax compliance guide',
    udience: [
      { '@type': 'Audience', audienceType: 'Tax Professionals' },
      { '@type': 'Audience', audienceType: 'Corporate Finance Teams' },
      { '@type': 'Audience', audienceType: 'Legal Advisors' },
      { '@type': 'Audience', audienceType: 'Business Owners' },
    ],
    ...(blogs.length > 0 && {
      blogPost: blogs.slice(0, 10).map((blog) => ({
        '@type': 'BlogPosting',
        '@id': `${baseUrl}/blogs/${blog.slug}`,
        headline: blog.title,
        description: blog.description,
        url: `${baseUrl}/blogs/${blog.slug}`,
        datePublished: formatDate(blog.publishedDate),
        author: {
          '@type': 'Person',
          name: blog.author,
        },
        image: blog.imageUrl || `${baseUrl}/web-app-manifest-512x512.png`,
        articleSection: blog.category,
      })),
    }),
  }
}

// Schema for FAQ section (not used till now, but we should have this for all docs and static pages)
export const generateBlogFAQSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How often is the GCC Tax Laws blog updated?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our blog is updated regularly with the latest tax developments, regulatory changes, and expert insights from across the GCC region. New articles are published weekly.',
        },
      },
      {
        '@type': 'Question',
        name: 'Who writes the articles on GCC Tax Laws blog?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our articles are written by experienced tax professionals and legal experts specializing in GCC tax laws and regulations.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I search for specific tax topics on the blog?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, you can search for specific tax topics using our search functionality or browse articles by category including Corporate Tax, VAT, International Tax, and more.',
        },
      },
    ],
  }
}

// Helper function to generate blog-specific keywords
const generateBlogKeywords = (blog) => {
  const keywords = new Set()

  // Add category-based keywords
  const categoryKeywords = {
    'Corporate Tax': [
      'corporate income tax',
      'CIT',
      'business tax',
      'company tax',
    ],
    VAT: ['value added tax', 'indirect tax', 'sales tax', 'consumption tax'],
    'International Tax': [
      'cross-border tax',
      'tax treaties',
      'transfer pricing',
      'BEPS',
    ],
    Others: [
      'excise tax',
      'customs duties',
      'economic substance',
      'compliance',
    ],
  }

  if (categoryKeywords[blog.category]) {
    categoryKeywords[blog.category].forEach((keyword) => keywords.add(keyword))
  }

  // Add GCC country keywords
  const countryKeywords = [
    'UAE',
    'Saudi Arabia(KSA)',
    'Qatar',
    'Kuwait',
    'Bahrain',
    'Oman',
    'GCC',
  ]
  countryKeywords.forEach((country) => keywords.add(`${country} tax`))

  // Add title-based keywords (extract meaningful words)
  if (blog.title) {
    const titleWords = blog.title
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(
        (word) =>
          word.length > 3 && !['the', 'and', 'for', 'with'].includes(word),
      )

    titleWords.slice(0, 5).forEach((word) => keywords.add(word))
  }

  // Add general tax keywords
  ;['tax law', 'tax compliance', 'tax regulation', 'tax update'].forEach(
    (keyword) => keywords.add(keyword),
  )

  return Array.from(keywords).slice(0, 15).join(', ')
}

// Schema for Website/Organization (Enhanced)
export const generateWebsiteSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `https://${getCurrentDomain()}/#website`,
    name: 'GCC Tax Laws',
    alternateName: 'GTL',
    description:
      'Searchable GCC tax database: UAE VAT & Corporate Tax (CIT) Saudi/KSA VAT customs duty excise transfer pricing and tax treaties (DTAA). Kuwait Oman Qatar Bahrain.', // Premier GCC Tax Research Platform for tax advisors, corporations, and academic institutions
    url: `https://${getCurrentDomain()}`,
    inLanguage: 'en',
    copyrightYear: new Date().getFullYear(),
    dateModified: new Date().toISOString(),
    potentialAction: [
      {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `https://${getCurrentDomain()}/search-across-law?country=UAE&law=cit-fdl-47-of-2022&search={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
      {
        '@type': 'ReadAction',
        target: `https://${getCurrentDomain()}/blogs`,
      },
    ],
    publisher: {
      '@type': 'Organization',
      '@id': `https://${getCurrentDomain()}/#organization`,
      name: 'GCC Tax Laws',
      url: `https://${getCurrentDomain()}`,
      logo: {
        '@type': 'ImageObject',
        url: `https://${getCurrentDomain()}/web-app-manifest-512x512.png`,
        width: 180,
        height: 60,
      },
      description:
        'Searchable GCC tax database: UAE VAT & Corporate Tax (CIT) Saudi/KSA VAT customs duty excise transfer pricing and tax treaties (DTAA). Kuwait Oman Qatar Bahrain.', // Leading provider of GCC tax law information and research
      foundingDate: '2025-06-01',
      knowsAbout: [
        'UAE CIT (FDL No 47 of 2022, as amended)',
        'UAE VAT (FDL No 8 of 2017, as amended)',
        'UAE Tax Procedures (FDL No 28 of 2022, as amended)',
        'UAE Excise Tax (FDL No 7 of 2017, as amended)',
        'UAE Foreign Account Tax Compliance Act 2024',
        'UAE CRS (Common Reporting Standard, Federal Decree No 48 of 2018)',
        'KSA Income Tax Law 2004',
        'KSA Zakat Collection Law 1951',
        'KSA Foreign Account Tax Compliance Act 2017',
        'Kuwait Draft Business Profit Tax Law 2024',
        'Kuwait Income Tax Law - Decree No. 3 of 1955',
        'Kuwait QDMTT Law - Decree No. 157 of 2024',
        'Qatar Income Tax Law 24 of 2018',
        'Qatar DMTT FDL No 22 of 2024',
        'Qatar Excise Law 2018',
        'Oman Income Tax Law (Royal Decree No 28 of 2009, as amended)',
        'Oman VAT Law 2020',
        'Bahrain DMTT Law (Decree Law No. 11 of 2024)',
        'Bahrain VAT Law 48 of 2018',
        'GCC Common Customs Agreement',
        'GCC Common VAT Agreement',
        'GCC Common Excise Agreement',
        'Country-by-Country Reporting Law',
        'Customs Duty Law',
        'Foreign Account Tax Compliance Act',
        'Corporate Income Tax',
        'Value Added Tax',
        'Tax Treaties (DTAA)',
        'Tax Compliance',
      ],
      areaServed: [
        {
          '@type': 'Country',
          name: 'United Arab Emirates',
        },
        {
          '@type': 'Country',
          name: 'Kingdom of Saudi Arabia',
        },
        {
          '@type': 'Country',
          name: 'Kuwait',
        },
        {
          '@type': 'Country',
          name: 'Qatar',
        },
        {
          '@type': 'Country',
          name: 'Oman',
        },
        {
          '@type': 'Country',
          name: 'Bahrain',
        },
        {
          '@type': 'Country',
          name: 'Gulf Cooperation Council',
        },
      ],
      serviceArea: {
        '@type': 'Country',
        name: 'Gulf Cooperation Council',
      },
      sameAs: [
        'https://linkedin.com/company/gcctaxlaws',
        'https://www.youtube.com/@gcctaxlaws',
      ],
    },
    mainEntity: {
      '@type': 'Organization',
      '@id': `https://${getCurrentDomain()}/#organization`,
    },
  }
}

// Schema for Organization
export const generateOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `https://${getCurrentDomain()}/#organization`,
    name: 'GCC Tax Laws',
    alternateName: 'GTL',
    url: `https://${getCurrentDomain()}`,
    logo: {
      '@type': 'ImageObject',
      url: `https://${getCurrentDomain()}/web-app-manifest-512x512.png`,
      width: 180,
      height: 60,
    },
    description:
      'Searchable GCC tax database: UAE VAT & Corporate Tax (CIT) Saudi/KSA VAT customs duty excise transfer pricing and tax treaties (DTAA). Kuwait Oman Qatar Bahrain.', // Premier GCC Tax Research Platform providing comprehensive tax law information
    foundingDate: '2025-06-01',
    knowsAbout: [
      'UAE CIT (FDL No 47 of 2022, as amended)',
      'UAE VAT (FDL No 8 of 2017, as amended)',
      'UAE Tax Procedures (FDL No 28 of 2022, as amended)',
      'UAE Excise Tax (FDL No 7 of 2017, as amended)',
      'UAE Foreign Account Tax Compliance Act 2024',
      'UAE CRS (Common Reporting Standard, Federal Decree No 48 of 2018)',
      'KSA Income Tax Law 2004',
      'KSA Zakat Collection Law 1951',
      'KSA Foreign Account Tax Compliance Act 2017',
      'Kuwait Draft Business Profit Tax Law 2024',
      'Kuwait Income Tax Law - Decree No. 3 of 1955',
      'Kuwait QDMTT Law - Decree No. 157 of 2024',
      'Qatar Income Tax Law 24 of 2018',
      'Qatar DMTT FDL No 22 of 2024',
      'Qatar Excise Law 2018',
      'Oman Income Tax Law (Royal Decree No 28 of 2009, as amended)',
      'Oman VAT Law 2020',
      'Bahrain DMTT Law (Decree Law No. 11 of 2024)',
      'GCC Common Customs Agreement',
      'GCC Common VAT Agreement',
      'GCC Common Excise Agreement',
      'Country-by-Country Reporting Law',
      'Customs Duty Law',
      'Foreign Account Tax Compliance Act',
      'Corporate Income Tax',
      'Value Added Tax',
      'Tax Treaties (DTAA)',
      'Tax Compliance',
    ],
    areaServed: [
      {
        '@type': 'Country',
        name: 'United Arab Emirates',
      },
      {
        '@type': 'Country',
        name: 'Kingdom of Saudi Arabia',
      },
      {
        '@type': 'Country',
        name: 'Kuwait',
      },
      {
        '@type': 'Country',
        name: 'Qatar',
      },
      {
        '@type': 'Country',
        name: 'Oman',
      },
      {
        '@type': 'Country',
        name: 'Bahrain',
      },
      {
        '@type': 'Country',
        name: 'Gulf Cooperation Council',
      },
    ],
    serviceArea: {
      '@type': 'Country',
      name: 'Gulf Cooperation Council',
    },
    sameAs: [
      'https://linkedin.com/company/gcctaxlaws',
      'https://www.youtube.com/@gcctaxlaws',
    ],
  }
}

// Enhanced Helper Functions

const extractFirstSentence = (content) => {
  if (!content) return ''

  // Remove HTML tags and get clean text
  const textOnly = content.replace(/<[^>]*>/g, '').trim()
  if (!textOnly) return ''

  // Find first sentence - look for period followed by space and capital letter, or end of string
  const sentenceEnd = textOnly.search(/\.\s+[A-Z]|\.$/)
  const firstSentence =
    sentenceEnd > 0
      ? textOnly.substring(0, sentenceEnd + 1)
      : textOnly.split('.')[0] + '.'

  // Limit to 160 characters for meta description
  return firstSentence.length > 160
    ? firstSentence.substring(0, 157) + '...'
    : firstSentence
}

const formatDate = (dateString) => {
  if (!dateString) return new Date().toISOString()
  try {
    return new Date(dateString).toISOString()
  } catch {
    return new Date().toISOString()
  }
}

const getWordCount = (content) => {
  if (!content) return 0
  const textOnly = content.replace(/<[^>]*>/g, '').trim()
  return textOnly ? textOnly.split(/\s+/).length : 0
}

const generateKeywords = (item, type) => {
  const baseKeywords = {
    article: [
      'tax law',
      'UAE tax',
      'GCC tax',
      'corporate tax',
      'VAT',
      'tax compliance',
    ],
    decision: [
      'tax decision',
      'FTA ruling',
      'tax authority',
      'UAE tax decision',
      'tax interpretation',
    ],
    guidance: [
      'tax guidance',
      'FTA guide',
      'tax procedures',
      'compliance guide',
      'tax help',
    ],
    treaty: [
      'tax treaty',
      'DTAA',
      'double taxation',
      'international tax',
      'bilateral agreement',
    ],
  }

  let keywords = [...(baseKeywords[type] || [])]

  // Add item-specific keywords
  if (item.title) {
    const titleWords = item.title
      .toLowerCase()
      .replace(/<[^>]*>/g, '')
      .split(/\s+/)
      .filter(
        (word) =>
          word.length > 3 && !['article', 'section', 'chapter'].includes(word),
      )
    keywords.push(...titleWords.slice(0, 3))
  }

  // Add number-based keywords
  if (item.number) {
    keywords.push(`${type} ${item.number}`)
  }

  // Add country-specific keywords
  if (item.slug) {
    const country = getCountryName(item.slug)
    if (country !== 'GCC Region') {
      keywords.push(`${country.toLowerCase()} tax`)
    }
  }

  return [...new Set(keywords)].join(', ')
}

const getCountryName = (slug) => {
  if (!slug) return 'GCC Region'
  if (slug.startsWith('uae-')) return 'United Arab Emirates'
  if (slug.startsWith('ksa-')) return 'Kingdom of Saudi Arabia'
  if (slug.startsWith('qatar-')) return 'Qatar'
  if (slug.startsWith('kuwait-')) return 'Kuwait'
  if (slug.startsWith('bahrain-')) return 'Bahrain'
  if (slug.startsWith('oman-')) return 'Oman'
  return 'GCC Region'
}

const getCountryCode = (slug) => {
  if (!slug) return 'gcc'
  if (slug.startsWith('uae-')) return 'uae'
  if (slug.startsWith('ksa-')) return 'ksa'
  if (slug.startsWith('qatar-')) return 'qatar'
  if (slug.startsWith('kuwait-')) return 'kuwait'
  if (slug.startsWith('bahrain-')) return 'bahrain'
  if (slug.startsWith('oman-')) return 'oman'
  return 'gcc'
}

const getIssuingAuthority = (slug) => {
  if (slug.startsWith('uae-')) return 'Federal Tax Authority'
  if (slug.startsWith('ksa-')) return 'Zakat, Tax and Customs Authority'
  if (slug.startsWith('qatar-')) return 'General Tax Authority - Qatar'
  if (slug.startsWith('kuwait-')) return 'Ministry of Finance - Kuwait'
  if (slug.startsWith('bahrain-'))
    return '	National Bureau for Revenue - Bahrain'
  if (slug.startsWith('oman-')) return 'Oman Tax Authority'
  return 'GCC Secretariat General'
}

const getLawName = (slug) => {
  if (!slug) return 'Tax Law'
  // Check for specific, most detailed slugs first
  if (slug.includes('cit-fdl-47-of-2022')) {
    return 'UAE CIT (FDL No 47 of 2022, as amended)'
  }
  if (slug.includes('dmtt-fdl-60-of-2023')) {
    return 'UAE DMTT (FDL No 60 of 2023)'
  }
  if (slug.includes('indirecttax-law-vat-uaevat-8-of-2017-as-amended')) {
    return 'UAE VAT (FDL No 8 of 2017, as amended)'
  }
  if (slug.includes('tp-fdl-28-of-2022-as-amended')) {
    return 'UAE Tax Procedures (FDL No 28 of 2022, as amended)'
  }
  if (slug.includes('indirect-excise-7-of-2017-as-amended')) {
    return 'UAE Excise Tax (FDL No 7 of 2017, as amended)'
  }
  if (slug.includes('fatca-of-2024')) {
    return 'UAE FATCA (Foreign Account Tax Compliance Act) 2024'
  }
  if (slug.includes('crs-federal-decree-48-of-2018')) {
    return 'UAE CRS (Common Reporting Standard, Federal Decree No 48 of 2018)'
  }
  if (slug.includes('draft-business-profit-tax-law-of-2024')) {
    return 'Kuwait Draft Business Profit Tax Law 2024'
  }
  if (slug.includes('kit-law-no-2-of-2008-amending-decree-no-3-of-1955')) {
    return 'Kuwait Income Tax Law - Decree No. 3 of 1955'
  }
  if (slug.includes('qdmtt-decree-law-157-of-2024')) {
    return 'Kuwait QDMTT Law - Decree No. 157 of 2024'
  }
  if (slug.includes('income-tax-law-of-2004')) {
    return 'KSA Income Tax Law 2004'
  }
  if (slug.includes('vat-law-m-113-of-2017')) {
    return 'KSA VAT Law 2017'
  }
  if (slug.includes('fatca-of-2017')) {
    return 'KSA FATCA (Foreign Account Tax Compliance Act) 2017'
  }
  if (slug.includes('qatar-dmtt-fdl-22-of-2024')) {
    return 'Qatar DMTT (FDL No 22 of 2024)'
  }
  if (slug.includes('income-tax-law-24-of-2018')) {
    return 'Qatar Income Tax Law 24 of 2018'
  }
  if (slug.includes('income-tax-law-royal-decree-28-of-2009')) {
    return 'Oman Income Tax Law (Royal Decree No 28 of 2009, as amended)'
  }
  if (slug.includes('vat-law-royal-decree-121-of-2020')) {
    return 'Oman VAT Law 2020'
  }
  if (slug.includes('excise-law-25-of-2018')) {
    return 'Qatar Excise Law 2018'
  }
  if (slug.includes('excise-law-of-2019')) {
    return 'KSA Excise Law 2019'
  }
  if (slug.includes('excise-tax-law-royal-decree-23-of-2019')) {
    return 'Oman Excise Tax Law (Royal Decree No 23 of 2019)'
  }
  if (slug.includes('dmtt-law-11-of-2024')) {
    return 'Bahrain DMTT Law (Decree Law No. 11 of 2024)'
  }
  if (slug.includes('vat-law-48-of-2018')) {
    return 'Bahrain VAT Law 48 of 2018'
  }
  // Check for broader, less specific slugs (order is important to avoid early match)
  if (slug.includes('common-customs-agreement')) {
    return 'GCC Common Customs Agreement'
  }
  if (slug.includes('common-vat-agreement')) {
    return 'GCC Common VAT Agreement'
  }
  if (slug.includes('common-excise-agreement')) {
    return 'GCC Common Excise Agreement'
  }
  if (slug.includes('cbcr-law')) {
    return 'Country-by-Country Reporting Law'
  }
  if (slug.includes('tax-procedures-law')) {
    return 'Tax Procedures Law'
  }
  if (slug.includes('customs-law')) {
    return 'Customs Law'
  }
  if (slug.includes('zakat-law-of-1951')) {
    return 'KSA Zakat Collection Law 1951'
  }
    if (slug.includes('fatca-of-2014-iga-m1')) {
      return 'Qatar FATCA (Foreign Account Tax Compliance Act) 2014'
    }
    if (slug.includes('fatca-of-2014-direct')) {
      return 'Oman FATCA (Foreign Account Tax Compliance Act) 2014'
    }
  if (slug.includes('fatca')) {
    return 'Foreign Account Tax Compliance Act'
  }
  if (slug.includes('vat')) {
    return 'VAT Law'
  }
  if (slug.includes('excise')) {
    return 'Excise Law'
  }
  if (slug.includes('income-tax-law')) {
    return 'Income Tax Law'
  }

  return 'GCC Tax Law'
}

const getLawShortName = (slug) => {
  if (!slug) return 'tax-law'
  if (slug.includes('common-customs-agreement'))
    return 'common-customs-agreement'
  if (slug.includes('common-vat-agreement')) return 'common-vat-agreement'
  if (slug.includes('common-excise-agreement')) return 'common-excise-agreement'
  if (slug.includes('cit-fdl-47')) return 'cit-fdl-47-of-2022'
  if (slug.includes('dmtt-fdl-60-of-2023')) return 'dmtt-fdl-60-of-2023'
  if (slug.includes('indirecttax-law-vat-uaevat-8-of-2017-as-amended'))
    return 'indirecttax-law-vat-uaevat-8-of-2017-as-amended'
  if (slug.includes('tp-fdl-28-of-2022-as-amended'))
    return 'tp-fdl-28-of-2022-as-amended'
  if (slug.includes('indirect-excise-7-of-2017-as-amended'))
    return 'indirect-excise-7-of-2017-as-amended'
  if (slug.includes('fatca-of-2024')) return 'fatca-of-2024'
  if (slug.includes('fatca-of-2017')) return 'fatca-of-2017'
  if (slug.includes('fatca-of-2014-iga-m1')) return 'fatca-of-2014-iga-m1'
  if (slug.includes('fatca-of-2014-direct')) return 'fatca-of-2014-direct'
  if (slug.includes('vat-law-48-of-2018')) return 'vat-law-48-of-2018'
  if (slug.includes('fatca')) return 'fatca'
  if (slug.includes('draft-business-profit-tax-law-of-2024'))
    return 'draft-business-profit-tax-law-of-2024'
  if (slug.includes('kit-law-no-2-of-2008-amending-decree-no-3-of-1955'))
    return 'kit-law-no-2-of-2008-amending-decree-no-3-of-1955'
  if (slug.includes('qdmtt-decree-law-157-of-2024'))
    return 'qdmtt-decree-law-157-of-2024'
  if (slug.includes('dmtt-fdl-22-of-2024')) return 'dmtt-fdl-22-of-2024'
  if (slug.includes('income-tax-law-royal-decree-28-of-2009')) return 'income-tax-law-royal-decree-28-of-2009'
  if (slug.includes('income-tax-law')) return 'income-tax-law'
  if (slug.includes('excise')) return 'excise-law'
  if (slug.includes('vat')) return 'vat-law'
  if (slug.includes('zakat')) return 'zakat-law'
  if (slug.includes('tax-procedures')) return 'tax-procedures-law'
  if (slug.includes('crs-federal-decree-48-of-2018')) return 'crs-federal-decree-48-of-2018'
  if (slug.includes('cbcr-law')) return 'cbcr-law'
  if (slug.includes('customs-law')) return 'customs-law'
  if (slug.includes('zakat-law-of-1951')) return 'zakat-law-of-1951'

  return 'tax-law'
}
